import type { FormContentItem, FormValues } from '@/components/Form/types';
import type {
  FormConditionStoryblok,
  FormStoryblok,
  FormValidationConditionStoryblok,
} from '@/types/component-types-sb';

export const defaultCondition = { show: true };

type ConditionTypes = '' | 'optional' | 'require' | 'show' | 'hide' | undefined;

export const compareValueOrArray = (fieldValue: Array<unknown> | unknown, compareValue: unknown) => {
  return Array.isArray(fieldValue) && fieldValue.length > 0
    ? fieldValue.includes(compareValue)
    : (fieldValue ?? '') === compareValue;
};

export const isEmptyValue = (value: Array<unknown> | unknown) => {
  if (typeof value === 'number' && value === 0) return false;
  if (Array.isArray(value)) {
    return (value as []).length === 0;
  } else {
    return !value;
  }
};

export const evaluateCondition = (
  condition: FormConditionStoryblok | FormValidationConditionStoryblok,
  formValues: FormValues
): boolean => {
  let conditionResult;

  // If the condition doesn't have a source property (FormValidationConditionStoryblok),
  // we can't evaluate it properly, so we return true to not block validation
  if (!condition.source) {
    return true;
  }

  if (!formValues || !Object.values(formValues).length || !formValues.hasOwnProperty(condition.source)) {
    return false;
  }

  const fieldValue = formValues[condition.source];

  switch (condition.component) {
    case 'FormCondition':
      switch (condition.condition) {
        case 'isEmpty':
          conditionResult = isEmptyValue(fieldValue);
          break;
        case 'notEmpty':
          conditionResult = !isEmptyValue(fieldValue);
          break;
        case 'equals':
          conditionResult =
            typeof fieldValue === 'boolean'
              ? fieldValue === (condition.value === 'true')
              : compareValueOrArray(fieldValue, condition.value);
          break;
        case 'notEquals':
          conditionResult =
            typeof fieldValue === 'boolean'
              ? fieldValue !== (condition.value === 'true')
              : !compareValueOrArray(fieldValue, condition.value);
          break;
        default:
          conditionResult = true;
          break;
      }
      break;
    case 'FormValidationCondition':
      switch (condition.condition) {
        case 'isEmpty':
          conditionResult = isEmptyValue(fieldValue);
          break;
        case 'notEmpty':
          conditionResult = !isEmptyValue(fieldValue);
          break;
        case 'equals':
          conditionResult =
            typeof fieldValue === 'boolean'
              ? fieldValue === (condition.value === 'true')
              : compareValueOrArray(fieldValue, condition.value);
          break;
        case 'notEquals':
          conditionResult =
            typeof fieldValue === 'boolean'
              ? fieldValue !== (condition.value === 'true')
              : !compareValueOrArray(fieldValue, condition.value);
          break;
        default:
          conditionResult = true;
          break;
      }
      break;
    default:
      conditionResult = true;
      break;
  }
  return conditionResult;
};

export const evaluateConditions = (
  conditions: (FormConditionStoryblok | FormValidationConditionStoryblok)[] | undefined,
  formValues: { [key: string]: unknown }
) => {
  if (!conditions || conditions.length === 0) {
    return true;
  }
  return conditions.every((condition) => {
    if (!formValues || !(condition.source in formValues)) {
      return false;
    }

    return evaluateCondition(condition, formValues);
  });
};

export const hasCondition = (condition: ConditionTypes, item: FormContentItem): boolean => {
  return !!item.conditions.filter((c: FormConditionStoryblok) => c.targetAction === condition).length;
};

export const meetsCondition = (
  condition: ConditionTypes,
  item: FormContentItem,
  values: FormValues,
  fallback: boolean = true
): boolean => {
  if (!item.conditions || !hasCondition(condition!, item)) {
    return fallback;
  }

  return item.conditions
    .filter((c: FormConditionStoryblok) => (!condition ? true : c.targetAction === condition))
    .every((c: FormConditionStoryblok) => {
      if (!values || !(c.source in values)) {
        return fallback;
      }
      return evaluateCondition(c, values);
    });
};

export const failsCondition = (
  condition: ConditionTypes,
  item: FormContentItem,
  values: FormValues,
  fallback: boolean = true
): boolean => !meetsCondition(condition, item, values, !fallback); // invert fallback cause return value is also inverted

export const getNestedConditions = (field: any, form: FormStoryblok): FormConditionStoryblok[] => {
  const findElementAndConditions = (
    element: any,
    field: any,
    parentConditions: FormConditionStoryblok[] = []
  ): FormConditionStoryblok[] | undefined => {
    // Recursive end state
    if (element._uid === field._uid) {
      return parentConditions.concat(element.conditions || []);
    }

    if (element.content) {
      for (const child of element.content) {
        const result = findElementAndConditions(child, field, parentConditions.concat(element.conditions || []));
        if (result) {
          return result;
        }
      }
    }

    if (element.fields) {
      for (const child of element.fields) {
        const result = findElementAndConditions(child, field, parentConditions.concat(element.conditions || []));
        if (result) {
          return result;
        }
      }
    }

    return undefined;
  };

  if (form && !!form.content?.length) {
    for (const page of form.content) {
      const result = findElementAndConditions(page, field);
      if (result) {
        return result;
      }
    }
  }

  return [];
};
